import type { TFunction } from 'i18next';

import { formatEventTimestamp } from '@klareospace/pharos-lib-front-utils';
import {
  HISTORY_VALUE_KIND,
  TASK_HISTORY_EVENT_CODE,
  type HistoryEntry,
  type HistoryValue,
} from '@klareospace/pharos-lib-types';
import type { HistoryFeedEntry } from '@klareospace/pharos-lib-web-utils';

import { resolveActorLabel, translateStatusReason } from '&/features/orders/utils';

// ─── Constants ────────────────────────────────────────────────────────────────

const KNOWN_EVENT_CODES: ReadonlySet<string> = new Set(Object.values(TASK_HISTORY_EVENT_CODE));

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MapHistoryEntriesOptions {
  /**
   * Translates `history.events.<code>` (`tasks` namespace `t`). Status-reason codes are
   * cross-namespace-read under `orders:statusReason.<code>` — they live in `orders`, not `tasks`.
   */
  readonly t: (key: string) => string;
  /**
   * Translates the shared "by {{name}}" actor line (resolved via `resolveActorLabel`) and the
   * system/unknown-actor fallback labels (`common` namespace `t`). Typed as `TFunction` since the
   * actor line needs interpolation support.
   */
  readonly tCommon: TFunction;
}

// ─── Mapping ──────────────────────────────────────────────────────────────────

/** Maps a paginated `HistoryEntry[]` (already most-recent-first) to `HistoryFeed`-ready entries. */
export function mapHistoryEntries(entries: HistoryEntry[], options: MapHistoryEntriesOptions): HistoryFeedEntry[] {
  return entries.map((entry, index) => mapHistoryEntry(entry, index, options));
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function mapHistoryEntry(entry: HistoryEntry, index: number, options: MapHistoryEntriesOptions): HistoryFeedEntry {
  return {
    actorLabel: resolveActorLabel(entry, options.tCommon),
    id: `${entry.code}-${entry.timestamp}-${index}`,
    label: resolveLabel(entry.code, options.t),
    newValue: formatValue(entry.newValue, options),
    previousValue: formatValue(entry.previousValue, options),
    timestamp: formatEventTimestamp(entry.timestamp),
  };
}

/** Known task history codes translate via `history.events.<code>`; an unrecognized code falls back to a safe generic label instead of rendering a raw key. */
function resolveLabel(code: string, t: (key: string) => string): string {
  if (!KNOWN_EVENT_CODES.has(code)) return t('tasks:history.unknownEvent');
  return t(`history.events.${code}`);
}

/** Applies the per-code formatting `HistoryFeed`'s formatter props can't express: status-reason translation. Every other kind passes through unchanged. */
function formatValue(value: HistoryValue | null, options: MapHistoryEntriesOptions): HistoryValue | null {
  if (value === null || value.kind !== HISTORY_VALUE_KIND.STATUS) return value;
  const tOrders = (key: string): string => options.t(`orders:${key}`);
  return { ...value, reason: translateStatusReason(tOrders, value.reason) };
}
